// THE REVIEW QUEUE (DESIGN §8, BUILD-BRIEF §4) — where "AI proposes, humans commit" is
// actually performed.
//
// Everything an agent, a monitor or the seed importer believes arrives here UNHELD. It becomes
// a fact, an affiliation, a touchpoint, a commitment, a person or an organisation only when the
// operator HOLDS it — one click, one applier, one `applied_ref` recording what the click wrote.
//
// TWO PANES, because one scroll of equal cards makes the claim that duplicates PER-0088 look
// exactly like the claim that is new ground:
//
//   the RAIL   every claim, one line each: its claim mark, its kind, its descriptor, and ONE
//              flag — the worst thing known about it (it collides · it cites nothing · it is
//              not on the record). Grouped under sticky heads by the principal that filed it,
//              because a run's claims are judged together.
//   the STAND  the selected claim at full dress, in a fixed order that answers the four
//              questions an operator actually asks before committing: what would enter the
//              record · does it validate · WHAT DOES THE RECORD ALREADY SAY (the collision
//              lane) · what is the evidence · why was it proposed · and, last, what exactly
//              this click writes.
//
// The vocabulary is unheld · hold · held · amended · refused, everywhere, without synonyms.
// "Accept" and "approve" do not appear in this file, because they do not appear in the record.
//
// Undo is a session verb: the server keeps an in-memory ledger of what THIS operator disposed
// of and what each disposition wrote, and walks it back exactly. A hold it cannot remember is
// refused in words rather than guessed at.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import ConfirmModal from '../components/ConfirmModal.js';
import EmptyState from '../components/EmptyState.js';
import {
  Att, Ev, Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sep, Sheet, Tag, Zone, ZoneHead,
  type RailTone,
} from '../components/PageKit.js';
import { useToast } from '../components/Toast.js';
import { usePoll } from '../components/usePoll.js';
import { useLocale, useT } from '../i18n/index.js';
import { href, open as openRoute, ROUTES, useDeepLink, useRouteParams } from '../routes.js';
import {
  proposalsApi, REFUSE_REASONS, REFUSE_REASON_LABEL,
  type AutoHoldSettings, type Check, type ProposalStand, type QueueItem, type ReviewSummary, type SessionEntry,
} from '../api/proposals.js';
import { request } from '../api/client.js';
// Organisation dedupe is a proposal like any other: the finder proposes a pair, the operator
// holds, amends or refuses it. The queue knows nothing about organisations — everything it needs
// to draw the pair is opened out of the generic stand by `api/dedupe.ts`.
import {
  DEDUPE_ACTIONS, DEDUPE_ACTION_WORD, DEDUPE_REASON_WORD, dedupePayloadOf, otherOf, pairOf, sideOf,
  type DedupeCard, type DedupePair, type DedupePayload, type DedupeReasonKind,
} from '../api/dedupe.js';
import type { ProposalKind, RefuseReason } from '../../shared/types.js';
// Round 5: a link proposal reads its role/basis/qualifier labels and its quantity's text out of
// the network vocabulary — the queue knows nothing about organisations besides this.
import {
  linkLabel, quantityText,
  type LinkBasis, type LinkQualifier, type LinkResidual, type LinkRole, type NetLang,
} from '../../shared/network.v1.js';
// Round 7 (§6.3): a person-cell proposal is read in the PERSON RASTER's own words — the cell's
// label and the kind that says how a value of it may come to be. The queue learns no vocabulary
// of its own; `personLabelFor` and `personKindLabel` are the one spelling of both.
import {
  PERSON_BIRTH_KEYS, findPersonCell, personKindLabel, personLabelFor,
  type FoundPersonCell,
} from '../../shared/person-raster.v1.js';

import './ReviewPage.de.js';
import './ReviewPage.fr.js';

type Vars = Record<string, string | number | null | undefined>;
type TFn = (key: string, vars?: Vars) => string;
type TpFn = (n: number, one: string, other: string, vars?: Vars) => string;
type DateFn = (iso: string | null | undefined) => string;

// ───────────────────────── the queue's own nouns ─────────────────────────

/** kind → the word the rail prints. English IS the key; the catalogues translate it. */
const KIND_WORD: Record<ProposalKind, string> = {
  fact: 'fact',
  affiliation: 'affiliation',
  touchpoint: 'touchpoint',
  commitment: 'commitment',
  persona: 'persona',
  draft: 'draft mail',
  candidate: 'candidate person',
  signal_action: 'signal action',
  organisation: 'organisation',
  person: 'person',
  // Round 2: a proposed change to a weight, a rule document or a skill body. It is held like
  // any other proposal, and its applier writes app_settings + settings_history, never a record
  // table (scaffold: one line, so the queue renders the new kind generically).
  rule_change: 'rule change',
  // Two organisation rows the finder believes are one. Its applier merges, relates or records a
  // dismissal — never silently, and never without this queue.
  dedupe: 'dedupe',
  // Round 3b: a website for an organisation that has none. Its applier appends the host to
  // `organisations.domains` and fetches the logo — one hold, both writes.
  domain: 'domain',
  // Round 5: a payer/referrer/funder/umbrella/partner/accreditor/vendor/consultant/auditor
  // relation between organisations. Its applier is the one writer of `organisation_links`.
  link: 'link',
};

/** interactions.kind → the word a touchpoint's second half prints. */
const TOUCHPOINT_WORD: Record<string, string> = {
  mail_in: 'mail in', mail_out: 'mail out', meeting: 'meeting', call: 'call',
  note: 'note', post: 'post', signal: 'signal',
};

const payloadOf = (item: { payload?: Record<string, unknown> | null }): Record<string, unknown> =>
  (item.payload ?? {}) as Record<string, unknown>;

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** The rail's first line: the kind, and the one qualifier that makes it readable at a glance. */
function kindLine(item: QueueItem, t: TFn, lang: NetLang): string {
  const p = payloadOf(item);
  const base = t(KIND_WORD[item.kind] ?? item.kind);
  if (item.kind === 'fact') return `${base} · ${item.flag === 'collides' ? t('update') : t('new')}`;
  if (item.kind === 'touchpoint' && TOUCHPOINT_WORD[str(p.kind)]) return `${base} · ${t(TOUCHPOINT_WORD[str(p.kind)])}`;
  if (item.kind === 'commitment' && (p.owner === 'us' || p.owner === 'them')) return `${base} · ${t(str(p.owner))}`;
  if (item.kind === 'affiliation') return `${base} · ${p.is_current === false ? t('past role') : t('current role')}`;
  if (item.kind === 'dedupe') {
    const d = dedupePayloadOf(p);
    return d ? `${base} · ${t(DEDUPE_ACTION_WORD[d.action])}` : base;
  }
  // A domain proposal IS its host: the rail names it on the first line, because that is the one
  // thing the operator judges before opening the row.
  if (item.kind === 'domain') return str(p.domain) ? `${base} · ${str(p.domain)}` : base;
  // Round 5: `link · payer`, the role read through `linkLabel`; `link · update` when the slot
  // this would land in already holds a current row (the same collision the fact branch reads).
  if (item.kind === 'link') {
    if (item.flag === 'collides') return `${base} · ${t('update')}`;
    const role = str(p.role);
    return role ? `${base} · ${linkLabel(lang, 'role', role)}` : base;
  }
  return base;
}

/** The group head's own line (BUILD-BRIEF-R6 §5.2): a source principal reads `source · <key>`
 *  rather than the bare `source:<key>` a raw `principal` string carries — the word and the key
 *  the operator actually judges, not the wire format. Every other principal is unchanged. */
function principalLine(principal: string, t: TFn): string {
  if (principal.startsWith('source:')) return `${t('source')} · ${principal.slice('source:'.length)}`;
  return principal;
}

/** The two joined fields `SUBJECT_JOIN`/`SUBJECT_SELECT` add for a `link` proposal (the
 *  `payload_organisation_name` precedent) — not on the shared `Proposal` type, which wave 1
 *  owns, so read defensively off the raw row rather than widen it here. */
const counterpartNameOf = (item: QueueItem): string | null =>
  (item as unknown as { payload_counterpart_name?: string | null }).payload_counterpart_name ?? null;
const counterpartRefOf = (item: QueueItem): string | null =>
  (item as unknown as { payload_counterpart_ref?: string | null }).payload_counterpart_ref ?? null;

/** A `link` payload's typed fields, read defensively off the raw `Record<string, unknown>`.
 *  `proposalSubjectId` is the proposal ROW's own `subject_id` (the call's subject, before
 *  `prepareLink` resolves an umbrella-member override) — `linkFieldsOf` needs it because
 *  `prepareLink` writes `subject_id: filedSubjectId` into EVERY prepared payload
 *  (propose_link.ts, always-true `p.subject_id != null`), so the payload alone cannot tell a
 *  plain link from the umbrella-member case (§3.2 step 8(c)): only a payload subject_id that
 *  DIFFERS from the row's own subject_id means the payload named a subject of its own. */
function linkFieldsOf(p: Record<string, unknown>, proposalSubjectId?: number | null) {
  const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
  return {
    role: (p.role ? String(p.role) : null) as LinkRole | null,
    basis: (p.basis ? String(p.basis) : null) as LinkBasis | null,
    quantity: num(p.quantity),
    qualifier: (p.qualifier ? String(p.qualifier) : null) as LinkQualifier | null,
    year: num(p.year),
    regime: p.regime ? String(p.regime) : null,
    residual: (p.residual ? String(p.residual) : null) as LinkResidual | null,
    method: p.method ? String(p.method) : null,
    namedInQuote: p.named_in_quote === undefined ? null : Boolean(p.named_in_quote),
    // §3.2 step 8(c)'s own condition: the payload's subject_id differs from the proposal row's
    // subject_id. Without a `proposalSubjectId` to compare against, this can't be told apart
    // from the ordinary case and reads false (the common, non-umbrella sentence).
    subjectNamedByPayload: proposalSubjectId != null
      && p.subject_id !== undefined && p.subject_id !== null
      && Number(p.subject_id) !== Number(proposalSubjectId),
  };
}

/** The counterpart's label — the record's own name, a regime alone, or a residual. Never more
 *  than one of the three, because `prepare` refused any payload that named two (§1.3). */
function linkCounterpartLabel(item: QueueItem, f: ReturnType<typeof linkFieldsOf>, lang: NetLang, t: TFn): string {
  const name = counterpartNameOf(item) ?? counterpartRefOf(item);
  if (name) return name;
  if (f.regime) return `${linkLabel(lang, 'regime', f.regime)} ${t('(regime)')}`;
  if (f.residual) return linkLabel(lang, 'residual', f.residual);
  return '';
}

/** The rail's second line: what the claim actually says, in the record's own words. */
function descriptorOf(item: QueueItem, t: TFn, fmtDate: DateFn, lang: NetLang): string {
  const p = payloadOf(item);
  const who = item.subject_label ?? item.payload_person_name ?? null;
  const org = item.payload_organisation_name;
  const join = (...parts: Array<string | null | undefined>): string =>
    parts.map((x) => (x ? String(x) : '')).filter(Boolean).join(' · ');

  switch (item.kind) {
    case 'fact':
      return join(who, `${str(p.key)}${p.value ? ` = ${str(p.value)}` : ''}`);
    case 'affiliation':
      return join(who, str(p.role_title), org);
    case 'touchpoint':
      return join(str(p.title), p.occurred_on ? fmtDate(str(p.occurred_on)) : null);
    case 'commitment':
      return join(str(p.text), p.due_on ? fmtDate(str(p.due_on)) : null);
    case 'person':
    case 'candidate':
      return join(str(p.name), str(p.role_title), org);
    case 'organisation':
      return join(str(p.name), str(p.seat) || str(p.canton));
    case 'domain':
      // The organisation the host would be written onto, and how it was found — the two things
      // that decide whether this host is really theirs.
      return join(org ?? item.subject_label, p.source ? t('found by {source}', { source: t(str(p.source)) }) : null);
    case 'link': {
      const f = linkFieldsOf(p);
      const quantity = f.basis && f.quantity !== null ? quantityText(lang, f.basis, f.quantity, f.qualifier) : null;
      return join(who, f.role ? linkLabel(lang, 'role', f.role) : null, linkCounterpartLabel(item, f, lang, t), quantity, f.year ? String(f.year) : null);
    }
    default: {
      const first = Object.values(p).find((v) => typeof v === 'string' && v.trim());
      return join(who, first ? String(first) : t('no descriptor was filed'));
    }
  }
}

/**
 * The rail's second line where a string will not do. A dedupe row names BOTH organisations and
 * prints the finder's score as a figure — a score is a quantity, so it is `<Fig>`, never mono.
 */
function descriptorNode(item: QueueItem, t: TFn, fmtDate: DateFn, fmtNum: (n: number, d?: number) => string, lang: NetLang): ReactNode {
  if (item.kind !== 'dedupe') return descriptorOf(item, t, fmtDate, lang);
  const p = payloadOf(item);
  const d = dedupePayloadOf(p);
  // The list does not carry the pair — that is the stand's job — so the rail names each side
  // with the best it has: a name the filing carried, the subject the queue resolved, a ref, and
  // only then the bare id. It never invents a name.
  const side = (name: unknown, ref: unknown, id: unknown, resolved: string | null): string =>
    str(name) || resolved || str(ref) || `#${str(id)}`;
  const aResolved = d && item.subject_id === d.a_id ? item.subject_label ?? item.subject_ref : null;
  const bResolved = d && item.subject_id === d.b_id ? item.subject_label ?? item.subject_ref : null;
  return (
    <>
      {side(p.a_name, p.a_ref, p.a_id, aResolved)} ↔ {side(p.b_name, p.b_ref, p.b_id, bResolved)}
      {d && d.score !== null ? <> <span className="sep">·</span> <Fig>{fmtNum(d.score, 2)}</Fig></> : null}
    </>
  );
}

const FLAG_WORD: Record<string, string> = {
  collides: 'collides',
  no_evidence: 'no evidence',
  not_on_record: 'not on record',
};

const VERDICT_WORD: Record<string, string> = {
  held: 'held', amended: 'amended', refused: 'refused', unheld: 'unheld',
};

/** One validation line, in the operator's language. The server sends codes, never sentences. */
function checkLine(c: Check, t: TFn, tp: TpFn): string {
  const detail = c.detail ?? '';
  switch (c.code) {
    case 'evidence_present': return tp(Number(detail) || 0, 'The filing carries {n} piece of evidence.', 'The filing carries {n} pieces of evidence.');
    case 'no_evidence': return t('Nothing was cited. This claim is asserted, not evidenced.');
    case 'evidence_cited': return t('Every citation names its source or quotes it verbatim.');
    case 'evidence_uncited': return t('The evidence names no source and quotes nothing.');
    case 'payload_complete': return t('The filing carries every field the write needs.');
    case 'payload_incomplete': return t('The filing is missing {fields}. Holding it would be refused.', { fields: detail });
    case 'applier_registered': return t('A {kind} write is registered; holding this performs it.', { kind: t(KIND_WORD[detail as ProposalKind] ?? detail) });
    case 'no_applier': return t('Nothing can hold a {kind} proposal yet. Nothing would be written.', { kind: t(KIND_WORD[detail as ProposalKind] ?? detail) });
    case 'collision_clean': return t('Nothing on the record answers to this yet.');
    case 'collides': return tp(Number(detail) || 0, 'The record already holds {n} row this would land beside.', 'The record already holds {n} rows this would land beside.');
    case 'collision_unchecked': return t('The collision check did not run. This claim has NOT been checked against the record.');
    default: return c.code;
  }
}

/**
 * A dedupe hold, spelled out. The merge sentence names EVERY limb that moves and says plainly
 * that the session ledger cannot walk it back: an archived organisation keeps `merged_into`, and
 * unpicking it is a new decision, not an undo.
 *
 * A limb the server did not count reads "not collected" — never a zero, because a zero here
 * would be a claim that nothing moves.
 */
function dedupeWritePreview(d: DedupePayload, pair: DedupePair | null, t: TFn, tp: TpFn): string {
  const survivor = sideOf(pair, d.survivor_id);
  const other = otherOf(pair, d.survivor_id);
  const aName = survivor ? survivor.ref : `#${d.survivor_id}`;
  const bName = other ? other.ref : `#${d.survivor_id === d.a_id ? d.b_id : d.a_id}`;
  if (d.action === 'relate') return t('Holding this sets {b}’s parent to {a}.', { a: aName, b: bName });
  if (d.action === 'dismiss') return t('Holding this records that the two are not the same and hides the pair from future runs.');
  const count = (n: number | null | undefined, one: string, many: string, absent: string): string =>
    (n === null || n === undefined ? t(absent) : tp(n, one, many));
  const moves = other
    ? [
        count(other.people_count, '{n} affiliation', '{n} affiliations', 'affiliations not collected'),
        count(other.interactions_count, '{n} interaction', '{n} interactions', 'interactions not collected'),
        count(other.commitments_count, '{n} commitment', '{n} commitments', 'commitments not collected'),
        count(other.open_deals, '{n} deal', '{n} deals', 'deals not collected'),
        count(other.facts_count, '{n} fact', '{n} facts', 'facts not collected'),
      ].join(' · ')
    : t('everything that points at it');
  return t('Holding this archives {b} into {a}: {moves} move; {bname} becomes an alias of {aname}. This cannot be undone from here; the archived row keeps merged_into.', {
    a: aName, b: bName, moves,
    aname: survivor?.name ?? aName,
    bname: other?.name ?? bName,
  });
}

/** The last section of the stand: what this click writes, in full, before it is clicked. */
/** The two bases whose quantity is a bare share and reads better with the thing it is a share
 *  of named after it — the "of revenue" i18n row this round adds, plus its "of participants"
 *  twin (the table gives only the first; the second follows the same pattern). */
const BASIS_OF_WORD: Partial<Record<LinkBasis, string>> = {
  participants_pct: 'of participants',
  revenue_pct: 'of revenue',
};

/** `quantityText` plus, for the two share bases, what the share is of. */
function quantityPhrase(lang: NetLang, f: ReturnType<typeof linkFieldsOf>, t: TFn): string | null {
  if (!f.basis || f.quantity === null) return null;
  const qty = quantityText(lang, f.basis, f.quantity, f.qualifier);
  const of = BASIS_OF_WORD[f.basis];
  return of ? `${qty} ${t(of)}` : qty;
}

function writePreview(stand: ProposalStand, t: TFn, tp: TpFn, lang: NetLang, override?: Record<string, unknown>): string {
  const item = stand.proposal;
  const p = override ?? payloadOf(item);
  const collides = stand.collisions.length > 0;
  const nextRef = stand.next_ref ?? '';
  switch (item.kind) {
    case 'fact':
      return collides
        ? t('{ref} {key} · one fact revision with its evidence link · one supersession. Nothing else moves.', { ref: item.subject_ref ?? '', key: str(p.key) })
        : t('{ref} {key} · one fact with its evidence link. Nothing else moves.', { ref: item.subject_ref ?? '', key: str(p.key) });
    case 'affiliation':
      return t('the proposed affiliation at {org} flips from proposed to held. Nothing else moves.', {
        org: item.payload_organisation_name ?? item.payload_organisation_ref ?? '',
      });
    case 'touchpoint':
      return t('one interaction {ref} with its participants. Nothing else moves.', { ref: nextRef });
    case 'commitment':
      return t('one open commitment {ref}, owed by {owner}. Nothing else moves.', { ref: nextRef, owner: t(str(p.owner)) });
    case 'person':
    case 'candidate':
      return p.organisation_id
        ? t('one person {ref} with status held, and one held affiliation. Nothing else moves.', { ref: nextRef })
        : t('one person {ref} with status held. Nothing else moves.', { ref: nextRef });
    case 'organisation':
      return t('one organisation {ref}. Nothing else moves.', { ref: nextRef });
    case 'draft':
      return t('one outbox row, status approved, addressed to {to}. It is not sent — sending is a separate click.', {
        to: str(Array.isArray(p.to) ? (p.to as unknown[]).join(', ') : p.to),
      });
    case 'signal_action':
      return (str(p.action) === 'draft' || str(p.disposition) === 'draft')
        ? t('the signal is stamped dispositioned, and one chained draft proposal is filed for the operator to hold separately. Nothing is sent.')
        : nextRef
          ? t('one open commitment {ref}, owed by us, and the signal is stamped dispositioned. Nothing else moves.', { ref: nextRef })
          : t('one open commitment, owed by us, and the signal is stamped dispositioned. Nothing else moves.');
    case 'persona': {
      const traitCount = Array.isArray(p.traits) ? p.traits.length : 0;
      return t('one held personas row with {n} trait(s); any previous held version for this subject is superseded. Nothing else moves.', { n: traitCount });
    }
    case 'rule_change': {
      const target = str(p.target);
      return target.startsWith('skill:')
        ? t('one skill-body file written beside the repo (not into it) and one settings_history row for {target}. Nothing else moves.', { target })
        : t('one app_settings row for scoring.rules and one settings_history row; a rescore is queued. Nothing else moves.');
    }
    case 'dedupe': {
      const d = dedupePayloadOf(p);
      return d
        ? dedupeWritePreview(d, pairOf(stand), t, tp)
        : t('nothing — the filing does not name a pair, so the hold would be refused.');
    }
    case 'domain':
      // Two writes under one click, and the operator is told about both before it is made: the
      // host joins `organisations.domains`, and the logo pass runs for that organisation.
      return t('Holding this sets the website and fetches the logo: {host} is appended to {org}’s domains, and the logo pass runs for it. Nothing else moves.', {
        host: str(p.domain),
        org: item.payload_organisation_name ?? item.subject_ref ?? item.payload_organisation_ref ?? '',
      });
    case 'link': {
      const f = linkFieldsOf(p);
      const ref = item.subject_ref ?? '';
      const name = item.subject_label ?? '';
      const counterpart = linkCounterpartLabel(item, f, lang, t);
      const role = f.role ? linkLabel(lang, 'role', f.role) : '';
      const quantity = quantityPhrase(lang, f, t);
      if (collides) {
        const collision = stand.collisions.find((c) => c.kind === 'link');
        return t(
          'One link on {ref} {name}: {counterpart} · {role} · {quantity} ({year}), and the {cyear} row ({clabel}) is superseded, undoable. Nothing else moves.',
          {
            ref, name, counterpart, role, quantity: quantity ?? '', year: f.year ?? '',
            cyear: collision?.detail ?? f.year ?? '', clabel: collision?.label ?? '',
          },
        );
      }
      return quantity
        ? t('One link on {ref} {name}: {counterpart} · {role} · {quantity} ({year}). Nothing else moves.', { ref, name, counterpart, role, quantity, year: f.year ?? '' })
        : t('One link on {ref} {name}: {counterpart} · {role}. Nothing else moves.', { ref, name, counterpart, role });
    }
    default:
      return t('nothing — no applier is registered for {kind}, so the hold would be refused.', {
        kind: t(KIND_WORD[item.kind] ?? item.kind),
      });
  }
}

/** The rationale an agent filed with the claim, under whichever key it used. */
function rationaleOf(payload: Record<string, unknown>): string {
  for (const key of ['why', 'rationale', 'reason', 'notes', 'note', 'verification', 'source_note']) {
    const v = payload[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** Payload fields an operator may correct in place. Nested shapes are shown, never edited. */
function editableFields(payload: Record<string, unknown>): string[] {
  return Object.keys(payload).filter((k) => {
    const v = payload[k];
    return v === null || ['string', 'number', 'boolean'].includes(typeof v)
      || (Array.isArray(v) && v.every((x) => typeof x === 'string'));
  });
}

/** Payload fields `editableFields` cannot render — an object, or an array of objects. These
 *  never entered the "What would enter the record" panel at all (a rule_change's whole rules
 *  document, a persona's traits): the operator was asked to hold a document they could not
 *  see. They render read-only, in full, never as inputs. */
function structuredFields(payload: Record<string, unknown>): string[] {
  const editable = new Set(editableFields(payload));
  return Object.keys(payload).filter((k) => !editable.has(k) && payload[k] !== undefined);
}

/** An edited string, coerced back into the shape the field arrived in. */
function coerce(original: unknown, next: string): unknown {
  const trimmed = next.trim();
  if (Array.isArray(original)) return trimmed ? trimmed.split(',').map((s) => s.trim()).filter(Boolean) : [];
  if (typeof original === 'boolean') return trimmed === 'true';
  if (typeof original === 'number') {
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : trimmed;
  }
  return trimmed === '' ? null : trimmed;
}

const fieldToText = (v: unknown): string => (Array.isArray(v) ? v.join(', ') : v === null || v === undefined ? '' : String(v));

// ───────────────────────── dedupe: the pair, at full dress ─────────────────────────

/** One labelled line inside a dedupe card. An absent value says so; it never reads as empty. */
function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', marginTop: '4px' }}>
      <span className="u-label" style={{ flex: '0 0 84px' }}>{label}</span>
      <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
    </div>
  );
}

/**
 * One side of the pair. The two sit next to each other in the collision lane, because the
 * question a dedupe proposal asks IS "what does the record already say" — twice.
 */
function DedupeSide({ card, role, chosen }: { card: DedupeCard; role: string; chosen: boolean }) {
  const t = useT();
  const { fmtDate, fmtNum } = useLocale();
  const people = card.people ?? [];
  const shown = people.map((p) => (p.role_title ? `${p.name} — ${p.role_title}` : p.name)).join(' · ');
  const more = Math.max(0, (card.people_count ?? 0) - people.length);
  return (
    <div className={`nb${chosen ? ' hit' : ''}`} style={{ minWidth: 0 }}>
      <div className="lbl">
        <span className="u-label">{role}</span>
        <span className="r"><Ref>{card.ref}</Ref></span>
      </div>
      <div className="voice-record" style={{ margin: 0 }}>{card.name}</div>
      <Line label={t('domains')}>
        {card.domains?.length ? card.domains.map((d, i) => <Fragment key={d}>{i ? <span className="sep">·</span> : null}<Ref>{d}</Ref></Fragment>) : <span className="fv--unknown">{t('not collected')}</span>}
      </Line>
      <Line label={t('UID')}>{card.uid ? <Ref>{card.uid}</Ref> : <span className="fv--unknown">{t('not collected')}</span>}</Line>
      <Line label={t('canton')}>{card.canton ?? <span className="fv--unknown">{t('not collected')}</span>}</Line>
      <Line label={t('people')}>
        <Fig>{fmtNum(card.people_count ?? 0)}</Fig>
        {shown ? <> <span className="sep">·</span> {shown}{more ? <> <span className="sep">·</span> {t('and {n} more', { n: more })}</> : null}</> : null}
      </Line>
      <Line label={t('open deals')}><Fig>{fmtNum(card.open_deals ?? 0)}</Fig></Line>
      <Line label={t('facts')}><Fig>{fmtNum(card.facts_count ?? 0)}</Fig></Line>
      <Line label={t('created')}>
        {card.created_by_kind ? t(card.created_by_kind) : <span className="fv--unknown">{t('not collected')}</span>}
        {card.created_at ? <> <span className="sep">·</span> <Fig>{fmtDate(card.created_at)}</Fig></> : null}
      </Line>
    </div>
  );
}

// ───────────────────────── round 7: a PERSON CELL in the queue (§6.3) ─────────────────────────

/**
 * The person-raster cell a claim names, or null when it names none.
 *
 * A person's cells are keyed `person.<block>.<cell>`; `findPersonCell` also accepts the short
 * forms the canonicaliser leaves behind, which is why the key goes through it rather than
 * through a `startsWith`. A fact on a person that answers no cell — a free key the operator
 * allowed (§3.1) — is not a cell and is read exactly as it was before this round.
 */
export function personCellOf(item: { subject_kind?: string | null; payload?: Record<string, unknown> | null }): FoundPersonCell | null {
  if (item.subject_kind !== 'person') return null;
  const key = str(payloadOf(item).key).trim();
  return key ? findPersonCell(key) : null;
}

/** Anything whose text names the broker. A value Surfe asserted is ALWAYS a person's click (§3.5). */
const SURFE = /surfe/i;

/**
 * The two cells §3.5 excludes from held-on-arrival, and anything a provider asserted: the note
 * beside the verb says so in the operator's own words. It is not a warning about the claim —
 * the claim may be perfectly good — it is a statement about WHO decides, which is the whole of
 * P1 in one line.
 */
export function alwaysYourClick(item: {
  subject_kind?: string | null;
  payload?: Record<string, unknown> | null;
  evidence?: Array<{ kind?: string | null; ref?: string | null; quote?: string | null }> | null;
}): boolean {
  const found = personCellOf(item);
  if (found && PERSON_BIRTH_KEYS.includes(found.key)) return true;
  const p = payloadOf(item);
  for (const field of ['source', 'provider', 'origin', 'via', 'evidence_ref', 'method_note', 'source_note']) {
    if (SURFE.test(str(p[field]))) return true;
  }
  return (item.evidence ?? []).some((e) => SURFE.test(str(e?.ref)) || SURFE.test(str(e?.kind)) || SURFE.test(str(e?.quote)));
}

// ───────────────────────── round 3: a run's raster cells as ONE group ─────────────────────────

/** A `fact` proposal for a raster cell, filed by a run: `profile.<block>.<cell>` under a run id. */
const isProfileCell = (item: QueueItem): boolean =>
  item.kind === 'fact' && item.run_id != null && item.subject_kind != null && item.subject_id != null
  && String(payloadOf(item).key ?? '').startsWith('profile.');

const profileGroupKey = (item: QueueItem): string => `${item.run_id}:${item.subject_kind}:${item.subject_id}`;

export interface ProfileGroup {
  key: string;
  run_id: number;
  subject_kind: 'person' | 'organisation';
  subject_id: number;
  subject_ref: string | null;
  subject_label: string | null;
  ids: number[];
  unheld: number;
  collides: number;
}

/** The profile groups inside one principal's items, keyed by run + subject. Exported for tests. */
export function profileGroupsOf(items: QueueItem[]): Map<string, ProfileGroup> {
  const out = new Map<string, ProfileGroup>();
  for (const item of items) {
    if (!isProfileCell(item)) continue;
    const key = profileGroupKey(item);
    const g = out.get(key) ?? {
      key, run_id: Number(item.run_id), subject_kind: item.subject_kind as 'person' | 'organisation', subject_id: Number(item.subject_id),
      subject_ref: item.subject_ref, subject_label: item.subject_label, ids: [], unheld: 0, collides: 0,
    };
    g.ids.push(item.id);
    if (item.status === 'unheld') g.unheld += 1;
    if (item.status === 'unheld' && item.flag === 'collides') g.collides += 1;
    out.set(key, g);
  }
  return out;
}

interface HoldGroupResult {
  held: Array<{ id: number; ref: string; key: string; applied_ref: string | null; undoable: boolean }>;
  collided: Array<{ id: number; ref: string; key: string; current_value: string | null; proposed_value: string | null }>;
}

// ───────────────────────── the page ─────────────────────────

type Tab = 'untreated' | 'treated';
type Mode = null | 'amend' | 'refuse';

interface QueueData {
  items: QueueItem[];
  total: number;
  summary: ReviewSummary;
  session: SessionEntry[];
}

/** The class names in his words, not the code's. The cost sentence comes from the server, which
 *  is where the rule lives; only the heading is spelled here. */
const GUARD_LABEL: Record<string, string> = {
  pending_dependency: 'Waiting on an organisation still in Review',
  broker: 'Surfe said it',
  birth: 'A birth or age cell',
  estimate_over_held: 'An estimate over a value already held',
  duplicate_name: 'A name already on the record',
  operator_declared: 'A value you typed yourself',
};

export default function ReviewPage() {
  const t = useT();
  const { fmtDate, fmtDateTime, fmtNum, tp, lang } = useLocale();
  const toast = useToast();

  const [tab, setTab] = useState<Tab>('untreated');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<string | null>(null);
  const [principalFilter, setPrincipalFilter] = useState<string | null>(null);
  const [cursorId, setCursorId] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [reasonNote, setReasonNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [standVersion, setStandVersion] = useState(0);

  // ── arrival intents (routes.ts law 2: one-shot, then cleared) ──
  const params = useRouteParams();
  useDeepLink(params, {
    status: (v) => { if (v === 'refused' || v === 'held' || v === 'amended') { setTab('treated'); setStatusFilter(v); } else if (v === 'unheld') { setTab('untreated'); setStatusFilter(null); } },
    kind: (v) => setKindFilter(v || null),
    principal: (v) => setPrincipalFilter(v || null),
    proposal: (v) => { const n = Number(v); if (Number.isInteger(n) && n > 0) setCursorId(n); },
  });

  // ── the read: one poll, three answers, so a slow summary never blanks the queue ──
  const load = useCallback(async (): Promise<QueueData> => {
    const status = tab === 'untreated' ? 'unheld' : (statusFilter ?? 'treated');
    const [list, summary, session] = await Promise.all([
      proposalsApi.list({ status, kind: kindFilter, principal: principalFilter, limit: 300 }),
      proposalsApi.summary(),
      proposalsApi.session(),
    ]);
    return { items: list.items, total: list.total, summary, session: session.items };
  }, [tab, statusFilter, kindFilter, principalFilter]);

  const poll = usePoll<QueueData>(load);
  const rows = useMemo(() => poll.data?.items ?? [], [poll.data]);
  const summary = poll.data?.summary ?? null;
  const ledger = poll.data?.session ?? [];

  // ── the cursor: the operator's own place in the queue ──
  useEffect(() => {
    if (!rows.length) return;
    setCursorId((current) => (current && rows.some((r) => r.id === current) ? current : rows[0].id));
  }, [rows]);

  useEffect(() => { setMode(null); setEdits({}); setReasonNote(''); }, [cursorId]);

  // ── the stand: fetched per claim, refetched after every disposition ──
  const [stand, setStand] = useState<ProposalStand | null>(null);
  const [standError, setStandError] = useState<string | null>(null);
  useEffect(() => {
    if (!cursorId) { setStand(null); setStandError(null); return; }
    let alive = true;
    proposalsApi.get(cursorId)
      .then((s) => { if (alive) { setStand(s); setStandError(null); } })
      .catch((err: unknown) => { if (alive) { setStand(null); setStandError(err instanceof Error ? err.message : String(err)); } });
    return () => { alive = false; };
  }, [cursorId, standVersion]);

  // The poll object is new on every render; its `refresh` is not. Holding it in a ref keeps
  // `refresh` — and with it every verb below, and the keyboard listener that closes over them —
  // stable, so the window listener is attached once instead of on each render.
  const refreshRef = useRef(poll.refresh);
  refreshRef.current = poll.refresh;
  const refresh = useCallback(async () => {
    await refreshRef.current();
    setStandVersion((v) => v + 1);
  }, []);

  const index = rows.findIndex((r) => r.id === cursorId);

  const moveCursor = useCallback((delta: number) => {
    if (!rows.length) return;
    const at = rows.findIndex((r) => r.id === cursorId);
    const next = Math.min(Math.max((at < 0 ? 0 : at) + delta, 0), rows.length - 1);
    setCursorId(rows[next].id);
  }, [rows, cursorId]);

  /** After a disposition the cursor stands on the next claim, not on the one that just left. */
  const advance = useCallback(() => {
    const at = rows.findIndex((r) => r.id === cursorId);
    const next = rows[at + 1] ?? rows[at - 1] ?? null;
    setCursorId(next ? next.id : null);
  }, [rows, cursorId]);

  // ── the four verbs ──

  const hold = useCallback(async (id: number, payload?: Record<string, unknown> | null) => {
    setBusy(true);
    try {
      const result = payload ? await proposalsApi.amend(id, payload) : await proposalsApi.hold(id);
      const base = payload
        ? t('Amended and held. {ref} wrote {applied}.', { ref: result.proposal.ref, applied: result.applied_ref ?? t('no ref') })
        : t('Held. {ref} wrote {applied}.', { ref: result.proposal.ref, applied: result.applied_ref ?? t('no ref') });
      const alsoHeld = result.also_held?.length
        ? ' ' + t('Also held first: {list}.', {
            list: result.also_held.map((h) => `${h.ref} → ${h.applied_ref ?? t('no ref')}`).join(', '),
          })
        : '';
      toast('ok', base + alsoHeld);
      setMode(null);
      advance();
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The hold failed. Nothing was written.'));
    } finally {
      setBusy(false);
    }
  }, [advance, refresh, t, toast]);

  const refuse = useCallback(async (id: number, reason: RefuseReason, note: string) => {
    setBusy(true);
    try {
      const result = await proposalsApi.refuse(id, reason, note || null);
      toast('ok', t('Refused: {reason}. {ref} wrote nothing.', { reason: t(REFUSE_REASON_LABEL[reason]), ref: result.proposal.ref }));
      setMode(null);
      setReasonNote('');
      advance();
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The refusal failed.'));
    } finally {
      setBusy(false);
    }
  }, [advance, refresh, t, toast]);

  const undo = useCallback(async (entry: SessionEntry) => {
    setBusy(true);
    try {
      const result = await proposalsApi.undo(entry.proposal_id);
      // The chain is not walked back with it: an organisation this click held FIRST stays on
      // the record, and the operator hears that in the same breath as the undo, not later.
      toast('ok', t('{ref} is unheld again.', { ref: entry.ref }) + (result.note ? ` ${result.note}` : ''));
      setCursorId(entry.proposal_id);
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The undo was refused.'));
    } finally {
      setBusy(false);
    }
  }, [refresh, t, toast]);

  const remove = useCallback(async (id: number) => {
    setBusy(true);
    try {
      const result = await proposalsApi.remove(id);
      toast('ok', t('{ref} is gone from the queue.', { ref: result.ref }));
      advance();
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The deletion failed.'));
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  }, [advance, refresh, t, toast]);

  /** Amend, then hold: the payload the operator corrected, coerced back into its own shapes. */
  const amendedPayload = useCallback((): Record<string, unknown> => {
    const base = payloadOf(stand?.proposal ?? { payload: {} });
    const next: Record<string, unknown> = { ...base };
    for (const [key, value] of Object.entries(edits)) next[key] = coerce(base[key], value);
    return next;
  }, [stand, edits]);

  // ── bulk hold: the mast verb, across every page the filters match (2026-09-06) ──
  //
  // Until today this walked the rows on screen one call each and was disabled the moment any of
  // them collided — with 1'500 rows in the queue, every time. Now the server holds what the list's
  // filters match, organisations first, one transaction per row, a slice per call; a row the
  // record already answers stays, with the reason on it, and the toast says how many and why.
  const unheldRows = useMemo(() => rows.filter((r) => r.status === 'unheld'), [rows]);
  const unheldTotal = tab === 'untreated' ? (poll.data?.total ?? unheldRows.length) : unheldRows.length;
  const [bulkProgress, setBulkProgress] = useState<{ held: number; total: number } | null>(null);
  // WHAT THE LAST RUN LEFT, so the operator can overrule it with the reasons in front of him
  // (his words, 08.09.2026: «the hold all doesn't work with "collides", i want to hold all»).
  const [leftBehind, setLeftBehind] = useState<{ total: number; reasons: Array<[string, number]> } | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  // ── the standing waiver (09.09.2026, «I need an auto accept feature») ──
  //
  // He cannot review 2'000 rows and should not have to. The blunt answer already existed — one
  // button that holds every guarded row — but among those rows are two where a machine reading
  // would supersede a value he typed himself, and a bulk press is the one thing that cannot show
  // him which two. So the guards are split into classes and he waives the ones he has already
  // decided: the rule then stops asking, of this backlog AND of every new arrival, which is the
  // part that keeps the queue from rebuilding to two thousand next week.
  const [autoHold, setAutoHold] = useState<AutoHoldSettings | null>(null);
  const [autoHoldOpen, setAutoHoldOpen] = useState(false);
  const [autoHoldBusy, setAutoHoldBusy] = useState(false);
  const openAutoHold = useCallback(async () => {
    setAutoHoldOpen(true);
    setAutoHoldBusy(true);
    try {
      setAutoHold(await proposalsApi.autoHold());
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The guards could not be counted.'));
      setAutoHoldOpen(false);
    } finally {
      setAutoHoldBusy(false);
    }
  }, [t, toast]);
  const toggleGuard = useCallback(async (guard: string) => {
    if (!autoHold) return;
    const next = autoHold.waived.includes(guard)
      ? autoHold.waived.filter((g) => g !== guard)
      : [...autoHold.waived, guard];
    setAutoHoldBusy(true);
    try {
      const saved = await proposalsApi.setAutoHold(next);
      setAutoHold({
        ...autoHold,
        waived: saved.waived,
        guards: autoHold.guards.map((g) => ({ ...g, waived: saved.waived.includes(g.guard) })),
      });
      toast('ok', saved.waived.length
        ? t('{n} guard(s) waived. New arrivals in those classes are held without asking.', { n: saved.waived.length })
        : t('Every guard is back on. Nothing is held without a person.'));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The waiver was not saved.'));
    } finally {
      setAutoHoldBusy(false);
    }
  }, [autoHold, t, toast]);

  const bulkHold = useCallback(async (includeAnswered = false) => {
    setBusy(true);
    const total = unheldTotal;
    let held = 0;
    let left = 0;
    let failed = 0;
    let sources = 0;
    let otherKinds = 0;
    let overruled = 0;
    /** Rows the server said were left to do but gave no way to reach — never silently dropped. */
    let stalled = 0;
    const reasons = new Map<string, number>();
    setBulkProgress({ held: 0, total });
    try {
      // THE SLICE IS A RESPONSE, NOT A LIMIT: the server works for a few seconds, says where it
      // stopped, and this asks again from there. The cursor is what makes the second call do
      // different work from the first — without it the server re-listed the queue every time and
      // spent the whole slice on the rows it had already failed to hold, so this loop ran for
      // ever with a count that never moved (the operator, 09.09.2026).
      let cursor: { rank: number; id: number } | null = null;
      for (let pass = 0; ; pass += 1) {
        const result = await proposalsApi.holdAll({ kind: kindFilter, principal: principalFilter, include_answered: includeAnswered, cursor });
        held += result.held.length;
        left += result.left.length;
        overruled += result.overruled.length;
        failed += result.failed.length;
        // The reason is row-specific («PER-0102 «Anne Dupuis» is already on the record»); the refs,
        // names and numbers are folded away so the same objection counts as one line.
        for (const row of [...result.left, ...result.overruled]) {
          const shape = row.why.replace(/PRO-\d+|ORG-\d+|PER-\d+/g, '…').replace(/«[^»]*»/g, '').replace(/#\d+/g, '').replace(/\b[a-z_]+\.[a-z_.]+\b/g, 'the cell').replace(/\s{2,}/g, ' ').trim();
          reasons.set(shape, (reasons.get(shape) ?? 0) + 1);
        }
        sources = result.not_a_candidate.sources;
        otherKinds = result.not_a_candidate.other_kinds;
        setBulkProgress({ held, total });
        if (result.remaining <= 0) break;
        // A server that reports work left but no place to resume from cannot be asked again: the
        // next call would be this one. Stop and say what was done rather than spin — and the same
        // if the cursor somehow did not move. A belt beside the server's own `i > 0` brace.
        const next = result.next_cursor;
        if (!next || (cursor && next.rank === cursor.rank && next.id === cursor.id)) {
          stalled = result.remaining;
          break;
        }
        cursor = next;
        void pass;
      }
      setLeftBehind(left ? { total: left, reasons: [...reasons.entries()].sort((a, b) => b[1] - a[1]) } : null);
      // WHEN NOTHING COULD BE HELD, SAY THAT FIRST AND NAME THE VERB THAT CAN (09.09.2026).
      // Every row remaining in his queue is guarded on purpose — birth cells, Surfe assertions,
      // names already on the record, dependants waiting on an organisation — so a correct Hold all
      // holds zero and used to report «0 held · 2138 left» as a mild warning. He read a button
      // called «Hold all 2249» doing nothing and reported it broken, which it was: not as logic,
      // as an interface. The count is not an answer; the reason and the way out are.
      const parts = held === 0 && left > 0
        ? [t('Nothing could be held automatically — every one of the {left} is guarded', { left })]
        : [t('{held} held', { held })];
      if (overruled) parts.push(t('{n} of them over the rule’s objection, noted on each row', { n: overruled }));
      if (left && held > 0) parts.push(t('{left} left — the record already answers them; the reason is on each row', { left }));
      if (failed) parts.push(t('{failed} refused the write', { failed }));
      if (sources) parts.push(t('{n} from a source stay for their own Hold all', { n: sources }));
      if (otherKinds) parts.push(t('{n} need a person (drafts, dedupes, rule changes)', { n: otherKinds }));
      if (stalled) parts.push(t('{n} not reached — press Hold all again', { n: stalled }));
      toast(left || failed || stalled ? 'warn' : 'ok', parts.join(' · '));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('Hold all stopped. What was held stays held; nothing else was written.'));
    } finally {
      setBulkProgress(null);
      setOverrideOpen(false);
      await refresh();
      setBusy(false);
    }
  }, [unheldTotal, kindFilter, principalFilter, refresh, t, toast]);

  // ── round 3: "Hold all cells" — a run's raster cells in one transaction ──
  const holdGroup = useCallback(async (group: ProfileGroup) => {
    setBusy(true);
    try {
      const result = await request<HoldGroupResult>('/proposals/hold-group', {
        method: 'POST',
        body: JSON.stringify({ run_id: group.run_id, subject_kind: group.subject_kind, subject_id: group.subject_id }),
      });
      toast(result.collided.length ? 'warn' : 'ok', t('{held} cells held · {collided} left for a per-cell decision', { held: result.held.length, collided: result.collided.length }));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The group hold failed. Nothing was written.'));
    } finally {
      setBusy(false);
    }
  }, [refresh, t, toast]);

  // ── round 6: "Hold all" on a source — every non-colliding proposal of one principal (§5.2) ──
  const holdPrincipalGroup = useCallback(async (principal: string) => {
    setBusy(true);
    try {
      const result = await request<HoldGroupResult>('/proposals/hold-group', {
        method: 'POST',
        body: JSON.stringify({ principal }),
      });
      toast(result.collided.length ? 'warn' : 'ok', t('{held} cells held · {collided} left for a per-cell decision', { held: result.held.length, collided: result.collided.length }));
      await refresh();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The group hold failed. Nothing was written.'));
    } finally {
      setBusy(false);
    }
  }, [refresh, t, toast]);

  // ── the keyboard: j/k a e r u, and Escape out of a mode ──
  const holdRef = useRef(hold);
  holdRef.current = hold;
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (typing) {
        if (event.key === 'Escape') { setMode(null); target?.blur(); }
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const k = event.key.toLowerCase();
      if (event.key === 'Escape') { setMode(null); return; }
      if (k === 'j' || event.key === 'ArrowDown') { event.preventDefault(); moveCursor(1); return; }
      if (k === 'k' || event.key === 'ArrowUp') { event.preventDefault(); moveCursor(-1); return; }
      if (k === 'u') {
        const last = ledger.find((e) => e.undoable);
        if (last) { event.preventDefault(); void undo(last); }
        return;
      }
      if (!cursorId || busy) return;
      const current = rows.find((r) => r.id === cursorId);
      if (!current || current.status !== 'unheld') return;
      // While the reasons row is open, `a` is not a hold: the operator is answering "why not".
      if (k === 'a') {
        if (mode === 'refuse') return;
        event.preventDefault();
        void holdRef.current(cursorId, mode === 'amend' ? amendedPayload() : null);
        return;
      }
      if (k === 'e') { event.preventDefault(); setMode((m) => (m === 'amend' ? null : 'amend')); return; }
      if (k === 'r') { event.preventDefault(); setMode((m) => (m === 'refuse' ? null : 'refuse')); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [amendedPayload, busy, cursorId, ledger, mode, moveCursor, rows, undo]);

  // ── the rail's five cells ──
  const railCells = useMemo(() => {
    const s = summary;
    const reasons = s ? Object.entries(s.refused_by_reason_7d) : [];
    const heldDetail = s
      ? t('{filed} as filed · {amended} amended', { filed: s.held_7d, amended: s.amended_7d })
      : t('not collected');
    return { s, reasons, heldDetail };
  }, [summary, t]);

  const { s, reasons, heldDetail } = railCells;

  const unheldTone: RailTone = !s ? 'unknown' : s.unheld > 0 ? 'attend' : 'nil';

  // ── grouping: sticky heads by the principal that filed the claims ──
  const groups = useMemo(() => {
    const out: Array<{ principal: string; items: QueueItem[]; profiles: Map<string, ProfileGroup> }> = [];
    for (const row of rows) {
      const last = out[out.length - 1];
      if (last && last.principal === row.principal) last.items.push(row);
      else out.push({ principal: row.principal, items: [row], profiles: new Map() });
    }
    // Round 3: a run's raster cells sit together under one head inside the principal's group.
    for (const g of out) {
      g.profiles = profileGroupsOf(g.items);
      if (!g.profiles.size) continue;
      const ordered: QueueItem[] = [];
      const placed = new Set<string>();
      for (const item of g.items) {
        if (!isProfileCell(item)) { ordered.push(item); continue; }
        const key = profileGroupKey(item);
        if (placed.has(key)) continue;
        placed.add(key);
        ordered.push(...g.items.filter((i) => isProfileCell(i) && profileGroupKey(i) === key));
      }
      g.items = ordered;
    }
    return out;
  }, [rows]);

  const current = rows.find((r) => r.id === cursorId) ?? null;
  const standPayload = stand ? payloadOf(stand.proposal) : {};
  const factCollision = stand?.collisions.find((c) => c.kind.startsWith('fact:')) ?? null;

  // ── round 6: the chained hold — dependencies this claim names, and dependents waiting on it ──
  // Only the still-unheld ones change anything: an already-held or refused dependency resolves
  // (or fails) on its own, exactly as it does today, so "Holds also" names only what this click
  // would ALSO hold.
  const pendingDeps = useMemo(
    () => (stand?.proposal.depends_on ?? []).filter((d) => d.status === 'unheld'),
    [stand],
  );
  const holdLabel = pendingDeps.length === 1 ? t('Hold both') : pendingDeps.length > 1 ? t('Hold all') : t('Hold it');
  const dependentsCount = stand?.proposal.kind === 'organisation' ? (stand.proposal.dependents_count ?? 0) : 0;

  // ── round 7 (§6.3): the person cell this claim names, and who decides it ──
  const personCell = stand ? personCellOf(stand.proposal) : null;
  const yourClick = stand ? alwaysYourClick(stand.proposal) : false;

  // ── dedupe: the pair, and the payload as the operator has it RIGHT NOW ──
  // While the amend row is open the write preview must describe what the amended click would
  // write, not what was filed — otherwise the operator reads a sentence about the wrong survivor.
  const isDedupe = stand?.proposal.kind === 'dedupe';
  const dedupePair = isDedupe ? pairOf(stand) : null;
  const effectivePayload = mode === 'amend' ? amendedPayload() : standPayload;
  const dedupe = isDedupe ? dedupePayloadOf(effectivePayload) : null;
  const dedupeSurvivor = sideOf(dedupePair, dedupe?.survivor_id ?? null);
  const dedupeOther = dedupe ? otherOf(dedupePair, dedupe.survivor_id) : null;
  const dedupeSides: DedupeCard[] = dedupePair ? [dedupePair.a, dedupePair.b] : [];
  /** merge names a survivor, relate names a parent, dismiss names neither. */
  const survivorWord = dedupe?.action === 'relate' ? 'parent' : 'survivor';

  const setDedupe = (patch: Partial<Pick<DedupePayload, 'action' | 'survivor_id'>>): void => {
    setEdits((prev) => ({
      ...prev,
      ...(patch.action ? { action: patch.action } : {}),
      ...(patch.survivor_id !== undefined ? { survivor_id: String(patch.survivor_id) } : {}),
    }));
  };

  // ── link: the fields the panel reads, and the two computed lines beneath it ──
  const isLink = stand?.proposal.kind === 'link';
  const linkFields = isLink ? linkFieldsOf(effectivePayload, stand!.proposal.subject_id) : null;
  const linkCounterpart = isLink && stand ? linkCounterpartLabel(stand.proposal, linkFields!, lang, t) : '';
  const linkCounterpartRef = isLink && stand && linkFields!.regime === null && linkFields!.residual === null
    ? counterpartRefOf(stand.proposal) : null;
  // §3.2 step 8: the sum check over the sibling unheld `link` proposals of the same run, subject,
  // basis and year. Two or more lines (this one plus at least one other) before it says anything —
  // a lone line has no distribution to check against.
  const linkSiblingSum = useMemo(() => {
    if (!isLink || !stand || !linkFields || linkFields.basis === null || linkFields.quantity === null) return null;
    if (linkFields.basis !== 'participants_pct' && linkFields.basis !== 'revenue_pct') return null;
    const runId = stand.proposal.run_id;
    const subjectId = stand.proposal.subject_id;
    if (runId == null || subjectId == null || linkFields.year === null) return null;
    const siblings = rows.filter((r) => {
      if (r.kind !== 'link' || r.status !== 'unheld' || r.run_id !== runId || r.subject_id !== subjectId) return false;
      const rf = linkFieldsOf(payloadOf(r));
      return rf.basis === linkFields.basis && rf.year === linkFields.year;
    });
    if (siblings.length < 2) return null;
    const sum = siblings.reduce((acc, r) => acc + (linkFieldsOf(payloadOf(r)).quantity ?? 0), 0);
    return { others: siblings.length - 1, sum: Math.round(sum * 100) / 100 };
  }, [isLink, stand, rows, linkFields]);

  return (
    <PageShell>
      <Mast
        kicker={t('Overview')}
        title={t('Review')}
        sub={
          <>
            {t('what the agents proposed and nobody has held yet')}
            <Sep />
            {t('nothing becomes a fact, a touchpoint or a sent mail without a click here')}
            <Sep />
            <Ref title={t('j moves down · k moves up · a holds · e amends · r refuses · u undoes')}>
              {t('j/k · a hold · e amend · r refuse · u undo')}
            </Ref>
          </>
        }
        verbs={
          <>
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy || !unheldTotal}
              title={t('Hold every unheld claim this list matches, across every page; a row the record already answers stays, with the reason on it')}
              onClick={() => { void bulkHold(); }}
            >
              {bulkProgress
                ? t('Holding… {held} of {total}', { held: bulkProgress.held, total: bulkProgress.total })
                : t('Hold all {n}', { n: unheldTotal })}
            </button>
            {/* THE OVERRIDE (his words, 08.09.2026). Offered only once a Hold all has actually left
                rows behind, so it never appears without the reasons that justify it. */}
            {leftBehind && !bulkProgress && (
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy}
                title={t('Hold the rows the rule guards too. Each one is written through the same applier and its objection is noted on the row.')}
                onClick={() => setOverrideOpen(true)}
              >
                {t('Hold the {n} left anyway', { n: leftBehind.total })}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy || autoHoldBusy}
              title={t('Choose which kinds of judgement the rule may stop asking about. A waived class is held automatically, on this backlog and on everything that arrives after.')}
              onClick={() => { void openAutoHold(); }}
            >
              {t('Auto-accept…')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => { setTab('treated'); setStatusFilter('refused'); }}
            >
              {t('Refusal log →')}
            </button>
          </>
        }
      />

      <Rail>
        <RailCell
          label={t('Unheld')}
          value={s ? fmtNum(s.unheld) : t('not collected')}
          tone={unheldTone}
          detail={s && s.unheld > 0
            ? t('oldest {date} · from {n} principals', { date: fmtDate(s.oldest_unheld), n: s.unheld_principals })
            : s ? t('the queue is empty') : t('the summary has not answered yet')}
        />
        <RailCell
          label={t('Held · 7d')}
          value={s ? fmtNum(s.held_7d + s.amended_7d) : t('not collected')}
          tone={s ? 'ink' : 'unknown'}
          detail={heldDetail}
        />
        <RailCell
          label={t('Refused · 7d')}
          value={s ? fmtNum(s.refused_7d) : t('not collected')}
          tone={s ? (s.refused_7d > 0 ? 'ink' : 'nil') : 'unknown'}
          detail={reasons.length
            ? reasons.map(([reason, n]) => `${n} ${t(REFUSE_REASON_LABEL[reason as RefuseReason] ?? reason)}`).join(' · ')
            : t('no refusal in the last seven days')}
        />
        <RailCell
          label={t('Edit distance')}
          value={s && s.edit_distance !== null ? fmtNum(s.edit_distance, 2) : t('not collected')}
          tone={s && s.edit_distance !== null ? 'ink' : 'unknown'}
          detail={s && s.edit_distance !== null
            ? t('how far your holds moved from the filed text')
            : t('nothing has been amended yet, so there is no distance to measure')}
        />
        <RailCell
          label={t('Hot run')}
          go={s && s.hot_run ? '→' : undefined}
          onClick={s && s.hot_run ? () => openRoute(ROUTES.run({ id: s.hot_run!.run_id })) : undefined}
          value={s && s.hot_run ? <Ref>{`#${s.hot_run.run_id}`}</Ref> : t('not collected')}
          unit={s && s.hot_run ? t('{n}% pushed back', { n: s.hot_run.pushed_back }) : undefined}
          tone={s && s.hot_run ? (s.hot_run.pushed_back > 20 ? 'attend' : 'ink') : 'unknown'}
          detail={s && s.hot_run
            ? t('{principal} · {filed} filed · {refused} refused', {
                principal: s.hot_run.principal, filed: s.hot_run.filed, refused: s.hot_run.refused,
              })
            : t('no run has filed anything yet')}
        />
      </Rail>

      {/* WHY NOTHING COULD BE HELD — on the page, not behind a dialog (09.09.2026).
          The reasons were always collected and always shown, inside the override's confirm modal,
          which he had to find and open first: the audit trail says «Hold the rest anyway» has
          never once been invoked. A Hold all that holds zero and explains itself only to someone
          who already knows where to look is a button that reports success at nothing. */}
      {leftBehind && !bulkProgress && (
        <Zone>
          <ZoneHead
            label={t('Why the queue did not move')}
            note={t('Every one of these was left on purpose. They are not errors, and none of them is waiting on the machine — each needs a decision only you can make.')}
          />
          <Sheet pad className="facts">
            {leftBehind.reasons.slice(0, 8).map(([why, n]) => (
              <div className="fact" key={why}>
                <span className="fk"><Fig>{n}</Fig></span>
                <span className="d">{why}</span>
                <span className="fmeta" />
              </div>
            ))}
          </Sheet>
        </Zone>
      )}

      <Zone>
        {poll.notice && (
          <ZoneHead label={t('The queue')} note={poll.notice} />
        )}
        {poll.error && !poll.data ? (
          <EmptyState
            kind="error"
            title={t('The queue could not be read')}
            detail={poll.error}
            onRetry={() => { void poll.refresh(); }}
          />
        ) : !rows.length && !poll.loading ? (
          <EmptyState
            kind={kindFilter || principalFilter || statusFilter ? 'no-match' : 'empty'}
            title={tab === 'untreated' ? t('Nothing is waiting for you') : t('Nothing has been treated yet')}
            detail={kindFilter || principalFilter || statusFilter
              ? t('The filter on this list excludes every claim. There are claims — just not these.')
              : t('A row appears here when an agent, a monitor or the seed importer files a proposal. Nothing writes to the record without passing through this queue.')}
            onClear={kindFilter || principalFilter || statusFilter
              ? () => { setKindFilter(null); setPrincipalFilter(null); setStatusFilter(null); }
              : undefined}
            clearLabel={t('Clear the filter')}
          />
        ) : (
          <Sheet strong clip className="panes">
            {/* ── the rail ── */}
            <div className="qrail">
              <div className="qtabs">
                <button
                  type="button"
                  className={`qtab${tab === 'untreated' ? ' on' : ''}`}
                  onClick={() => { setTab('untreated'); setStatusFilter(null); }}
                >
                  {t('Untreated')} <span className="n">{s ? s.unheld : '—'}</span>
                </button>
                <button
                  type="button"
                  className={`qtab${tab === 'treated' ? ' on' : ''}`}
                  onClick={() => { setTab('treated'); setStatusFilter(null); }}
                >
                  {t('Treated')} <span className="n">{s ? s.treated : '—'}</span>
                </button>
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {groups.map((group, gi) => {
                  const groupUnheld = group.items.filter((i) => i.status === 'unheld').length;
                  const isSource = group.principal.startsWith('source:');
                  return (
                  <div key={`${group.principal}-${gi}`}>
                    <div className="qgrp">
                      <Ref>{principalLine(group.principal, t)}</Ref>
                      <span>{t('{n} of {total}', { n: groupUnheld, total: group.items.length })}</span>
                      {isSource && groupUnheld > 0 && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={busy}
                          title={t('Hold every non-colliding proposal of this source in one transaction; a colliding row stays for its own click')}
                          onClick={(e) => { e.stopPropagation(); void holdPrincipalGroup(group.principal); }}
                        >
                          {t('Hold all')}
                        </button>
                      )}
                    </div>
                    {group.items.map((item) => (
                      <Fragment key={item.id}>
                        {(() => {
                          // Round 3: the first cell of a run's raster group carries the group head and its one verb.
                          if (!isProfileCell(item)) return null;
                          const pg = group.profiles.get(profileGroupKey(item));
                          if (!pg || pg.ids[0] !== item.id) return null;
                          return (
                            <div className="qgrp" key={`pg-${pg.key}`}>
                              <span>
                                {t('Profile')} <span className="sep">·</span> {pg.subject_ref ? <Ref>{pg.subject_ref}</Ref> : pg.subject_label}
                                <span className="sep">·</span> {tp(pg.ids.length, '{n} cell', '{n} cells')}
                              </span>
                              {pg.unheld > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  disabled={busy}
                                  title={t('Hold every non-colliding cell of this run in one transaction; a cell whose current value differs stays for a per-cell decision')}
                                  onClick={(e) => { e.stopPropagation(); void holdGroup(pg); }}
                                >
                                  {t('Hold all cells')}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        <button
                        type="button"
                        className={`qrow${item.id === cursorId ? ' on' : ''}`}
                        onClick={() => setCursorId(item.id)}
                      >
                        {item.status === 'unheld'
                          ? <Att kind="proposed" />
                          : item.status === 'refused' ? <span /> : <Att kind="held" />}
                        <span>
                          <span className="qk">{kindLine(item, t, lang)}</span>
                          <span className="qs">{descriptorNode(item, t, fmtDate, fmtNum, lang)}</span>
                        </span>
                        {item.status === 'unheld' ? (
                          item.flag ? (
                            <span className={`qflag ${item.flag === 'collides' ? 'attend' : item.flag === 'no_evidence' ? 'attend' : 'mute'}`}>
                              {t(FLAG_WORD[item.flag])}
                            </span>
                          ) : <span className="qflag mute">{t('clean')}</span>
                        ) : (
                          <span className="qflag mute">{t(VERDICT_WORD[item.status] ?? item.status)}</span>
                        )}
                      </button>
                      </Fragment>
                    ))}
                  </div>
                  );
                })}
              </div>
            </div>

            {/* ── the stand ── */}
            <div className="stand">
              {standError ? (
                <div className="stbody">
                  <EmptyState
                    kind="error"
                    title={t('This claim could not be read')}
                    detail={standError}
                    onRetry={() => setStandVersion((v) => v + 1)}
                  />
                </div>
              ) : !stand ? (
                <div className="stbody"><p className="rat">{t('Choose a claim on the left.')}</p></div>
              ) : (
                <>
                  <div className="sthd">
                    {stand.proposal.status === 'unheld' ? <Att kind="proposed" /> : stand.proposal.status === 'refused' ? <span /> : <Att kind="held" />}
                    <span className="kind">{kindLine(stand.proposal, t, lang)}</span>
                    {t('filed by')} <Ref>{stand.proposal.principal}</Ref> {t('on')}{' '}
                    <Fig>{fmtDateTime(stand.proposal.created_at)}</Fig>
                    {!stand.collisions_checked && (
                      <span className="chip" style={{ color: 'var(--broken)' }}>{t('not checked against the record')}</span>
                    )}
                    {stand.collisions_checked && stand.collisions.length > 0 && (
                      <span className="chip" style={{ color: 'var(--attend)' }}>{t('collides with a held value')}</span>
                    )}
                    <span className="ml-auto src-ref">{stand.proposal.ref}</span>
                  </div>

                  <div className="stbody">
                    {/* 1 — what would enter the record */}
                    <div className="sec">
                      <div className="lbl">
                        <span className="u-label">{t('What would enter the record')}</span>
                        <span className="r">
                          {stand.proposal.subject_ref && <><Ref>{stand.proposal.subject_ref}</Ref> · </>}
                          {stand.subject_label ?? stand.proposal.payload_person_name ?? stand.proposal.payload_organisation_name ?? t('a new row')}
                        </span>
                      </div>

                      {/* Round 7 §6.3: a person cell says WHICH cell in the raster's own words,
                          and what kind of value it is — an estimate, a look-up, or a name that
                          must be sourced or left alone. The raw `person.job.seniority` stays
                          below, in the payload, where an amend can still reach it. */}
                      {personCell && (
                        <p className="rat" style={{ marginTop: '4px' }}>
                          <span style={{ color: 'var(--ink-3)' }}>{t('cell')}: </span>
                          {personLabelFor(lang, personCell.block.key, personCell.cell.key)}{' '}
                          <Tag tone="ghost" title={personCell.cell.sources_hint}>
                            {personKindLabel(lang, personCell.cell.kind)}
                          </Tag>
                        </p>
                      )}

                      {pendingDeps.length > 0 && (
                        <p className="rat" style={{ marginTop: '4px' }}>
                          <span className="u-label">{t('Holds also')}</span>{' '}
                          {pendingDeps.map((d, i) => (
                            <Fragment key={d.id}>
                              {i > 0 ? <span className="sep">·</span> : null}
                              <Ref>{d.ref}</Ref>{d.name ? <> {d.name}</> : null} ({t(KIND_WORD[d.kind] ?? d.kind)}, {t('pending')})
                            </Fragment>
                          ))}
                        </p>
                      )}
                      {dependentsCount > 0 && (
                        <p className="rat" style={{ marginTop: '4px' }}>
                          {tp(dependentsCount, '{n} proposal waits on this', '{n} proposals wait on this')}
                        </p>
                      )}

                      {isDedupe ? (
                        // A dedupe filing is a decision, not a set of fields: the operator reads
                        // the action and the survivor, and amends them with the two controls
                        // below rather than by typing an id into a text box.
                        <div>
                          <div className="voice-record" style={{ margin: 0 }}>
                            {dedupe ? t(DEDUPE_ACTION_WORD[dedupe.action]) : t('The filing does not name a pair.')}
                            {dedupe && dedupe.action !== 'dismiss' && (
                              <>
                                {' '}<span className="sep">·</span>{' '}
                                <span style={{ color: 'var(--ink-3)' }}>{t(survivorWord)}: </span>
                                {dedupeSurvivor
                                  ? <><Ref>{dedupeSurvivor.ref}</Ref> {dedupeSurvivor.name}</>
                                  : <Ref>{`#${dedupe.survivor_id}`}</Ref>}
                              </>
                            )}
                            {dedupe && dedupe.score !== null && (
                              <> <span className="sep">·</span> <span style={{ color: 'var(--ink-3)' }}>{t('score')} </span><Fig>{fmtNum(dedupe.score, 2)}</Fig></>
                            )}
                          </div>
                          {mode === 'amend' && dedupe && (
                            <>
                              <div className="filter-chips" style={{ marginTop: '10px' }}>
                                {DEDUPE_ACTIONS.map((action) => (
                                  <button
                                    key={action}
                                    type="button"
                                    className={`chip${dedupe.action === action ? ' active' : ''}`}
                                    aria-pressed={dedupe.action === action}
                                    onClick={() => setDedupe({ action })}
                                  >
                                    {t(DEDUPE_ACTION_WORD[action])}
                                  </button>
                                ))}
                              </div>
                              {dedupe.action !== 'dismiss' && dedupeSides.length === 2 && (
                                <div className="filter-chips" style={{ marginTop: '6px' }}>
                                  <span className="u-label" style={{ alignSelf: 'center' }}>{t(survivorWord)}</span>
                                  {dedupeSides.map((side) => (
                                    <button
                                      key={side.id}
                                      type="button"
                                      className={`chip${dedupe.survivor_id === side.id ? ' active' : ''}`}
                                      aria-pressed={dedupe.survivor_id === side.id}
                                      onClick={() => setDedupe({ survivor_id: side.id })}
                                    >
                                      {side.ref} · {side.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <p className="rat">{t('What you hold is what is written; the filed text stays beside it in the ledger.')}</p>
                            </>
                          )}
                        </div>
                      ) : isLink && linkFields && mode !== 'amend' ? (
                        // A link filing is read the way a fact is: the subject and the
                        // counterpart, the role between them in words, the quantity as a figure,
                        // the year, the method — and the quote, because the one risk a link adds
                        // over a fact is a name resolved to an id (§3.2 step 8, §3.4).
                        <div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'stretch' }}>
                            <div className="nb" style={{ flex: '1 1 200px', minWidth: 0 }}>
                              <div className="voice-record" style={{ margin: 0 }}>
                                {stand.proposal.subject_ref && <><Ref>{stand.proposal.subject_ref}</Ref>{' '}</>}
                                {stand.subject_label ?? '—'}
                              </div>
                            </div>
                            <span style={{ alignSelf: 'center', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                              {linkFields.role ? linkLabel(lang, 'role', linkFields.role) : '—'}
                            </span>
                            <div className="nb" style={{ flex: '1 1 200px', minWidth: 0 }}>
                              <div className="voice-record" style={{ margin: 0 }}>
                                {linkCounterpartRef && <><Ref>{linkCounterpartRef}</Ref>{' '}</>}
                                {linkCounterpart || '—'}
                              </div>
                            </div>
                          </div>
                          <p className="rat" style={{ marginTop: '8px' }}>
                            {linkFields.basis && linkFields.quantity !== null && (
                              <><Fig>{quantityText(lang, linkFields.basis, linkFields.quantity, linkFields.qualifier)}</Fig> </>
                            )}
                            {linkFields.year && <><span className="sep">·</span> <Fig>{linkFields.year}</Fig> </>}
                            {linkFields.method && <><span className="sep">·</span> {t(linkFields.method)}</>}
                          </p>
                          {(stand.proposal.evidence ?? [])[0] && (
                            <Ev
                              src={
                                <>
                                  <Att kind="quoted" />
                                  {stand.proposal.evidence![0].ref ? <Ref>{stand.proposal.evidence![0].ref}</Ref> : <span>{t('no source named')}</span>}
                                </>
                              }
                            >
                              {stand.proposal.evidence![0].quote ? `« ${stand.proposal.evidence![0].quote} »` : t('The citation carries a source but no quoted span.')}
                            </Ev>
                          )}
                          {linkFields.namedInQuote === false && (
                            <p className="fv--attend" style={{ marginTop: '8px' }}>
                              {t(linkFields.subjectNamedByPayload
                                ? 'the quote does not name the subject — check the resolution'
                                : 'the quote does not name the counterpart — check the resolution')}
                            </p>
                          )}
                          {linkSiblingSum && (
                            <p className={linkSiblingSum.sum < 98 || linkSiblingSum.sum > 102 ? 'fv--attend' : ''} style={{ marginTop: '4px' }}>
                              {t('with the {n} other lines of this distribution: {sum} %', { n: linkSiblingSum.others, sum: linkSiblingSum.sum })}
                            </p>
                          )}
                        </div>
                      ) : mode === 'amend' ? (
                        <div>
                          {editableFields(standPayload).map((key) => (
                            <div className="form-group" key={key}>
                              <label className="form-label" htmlFor={`amend-${key}`}>{key}</label>
                              <input
                                id={`amend-${key}`}
                                className="form-input"
                                value={edits[key] ?? fieldToText(standPayload[key])}
                                onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                              />
                            </div>
                          ))}
                          <p className="rat">{t('What you hold is what is written; the filed text stays beside it in the ledger.')}</p>
                        </div>
                      ) : stand.proposal.kind === 'fact' && factCollision ? (
                        <p className="voice-record dif" style={{ margin: 0 }}>
                          {str(standPayload.key)}: <del>{factCollision.label}</del> <ins>{str(standPayload.value)}</ins>
                        </p>
                      ) : (
                        <div className="voice-record" style={{ margin: 0 }}>
                          {editableFields(standPayload).length === 0
                            ? t('The filing carries no readable field.')
                            : editableFields(standPayload).map((key) => (
                              <div key={key}>
                                <span style={{ color: 'var(--ink-3)' }}>{key}: </span>
                                {fieldToText(standPayload[key]) || '—'}
                              </div>
                            ))}
                        </div>
                      )}

                      {/* a dedupe's one structured field is `reasons`, and it is read as a list
                          under "Why it was proposed" — never as a JSON dump. */}
                      {(isDedupe ? [] : structuredFields(standPayload)).map((key) => (
                        <div key={key} style={{ marginTop: '10px' }}>
                          <span className="u-label" style={{ display: 'block', marginBottom: '4px' }}>{key}</span>
                          <pre
                            className="mono"
                            style={{
                              margin: 0, padding: '8px 10px', maxHeight: '260px', overflow: 'auto',
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                              background: 'var(--ink-6, rgba(0,0,0,.03))', border: '1px solid var(--line, rgba(0,0,0,.1))',
                            }}
                          >
                            {JSON.stringify(standPayload[key], null, 2)}
                          </pre>
                        </div>
                      ))}

                      <p className="rat" style={{ marginTop: '6px' }}>
                        {t('filed {date}', { date: fmtDate(stand.proposal.created_at) })}
                        {stand.next_ref ? <> <span className="sep">·</span> {t('would mint')} <Ref>{stand.next_ref}</Ref></> : null}
                        {/* THE RUN REF IS A DOOR (R3f §2): the claim on this card was filed by
                            a run, and the run's desk says what it read, what it dropped and why. */}
                        {stand.proposal.run_id ? (
                          <> <span className="sep">·</span>{' '}
                            <Lnk href={href(ROUTES.run({ id: stand.proposal.run_id }))} title={t('Open the desk of the run that filed this')}>
                              <Ref>{`run #${stand.proposal.run_id}`}</Ref>
                            </Lnk>
                          </>
                        ) : null}
                      </p>
                    </div>

                    {/* 2 — validation */}
                    <div className="sec">
                      <div className="lbl"><span className="u-label">{t('Validation')}</span></div>
                      {stand.checks.map((check, i) => (
                        <div className={`lint ${check.level === 'pass' ? 'pass' : 'warn'}`} key={`${check.code}-${i}`}>
                          <span className="lm">{check.level === 'pass' ? t('PASS') : t('WARN')}</span>
                          {checkLine(check, t, tp)}
                        </div>
                      ))}
                    </div>

                    {/* 3 — what the record already says */}
                    <div className="sec">
                      <div className="lbl">
                        <span className="u-label">{t('What the record already says')}</span>
                        <span className="r">
                          {isDedupe
                            ? t('two rows, side by side')
                            : !stand.collisions_checked
                              ? t('not checked')
                              : tp(stand.collisions.length, '{n} collision', '{n} collisions')}
                        </span>
                      </div>
                      {isDedupe ? (
                        dedupeSides.length === 2 ? (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                            {dedupeSides.map((side) => (
                              <DedupeSide
                                key={side.id}
                                card={side}
                                chosen={dedupe ? dedupe.action !== 'dismiss' && dedupe.survivor_id === side.id : false}
                                role={dedupe && dedupe.action !== 'dismiss'
                                  ? (dedupe.survivor_id === side.id
                                      ? t(survivorWord)
                                      : t(dedupe.action === 'merge' ? 'archived' : 'child'))
                                  : t('organisation')}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="nb hit">{t('The pair could not be read. Neither row is shown, so nothing here should be held.')}</div>
                        )
                      ) : !stand.collisions_checked ? (
                        <div className="nb hit">{t('The collision check did not run. This claim has NOT been checked against the record.')}</div>
                      ) : stand.collisions.length === 0 ? (
                        <div className="nb">{t('Nothing on the record answers to this yet.')}</div>
                      ) : (
                        stand.collisions.map((collision, i) => (
                          <div className="nb hit" key={`${collision.ref ?? ''}-${i}`} style={{ marginTop: i ? '6px' : 0 }}>
                            {collision.held ? <Att kind="held" /> : <Att kind="proposed" />}{' '}
                            {collision.ref && <><Ref>{collision.ref}</Ref>{' '}</>}
                            <span className="voice-record" style={{ fontSize: 'inherit' }}>{collision.label}</span>
                            {collision.detail && <> <span className="sep">·</span> <Fig>{collision.detail}</Fig></>}
                            {stand.proposal.kind === 'fact' && (
                              <> <span className="sep">·</span> {t('Holding this supersedes it; the old value stays in the history.')}</>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    {/* 4 — evidence */}
                    <div className="sec">
                      <div className="lbl">
                        <span className="u-label">{t('Evidence')}</span>
                        <span className="r">{tp(stand.proposal.evidence?.length ?? 0, '{n} span', '{n} spans')}</span>
                      </div>
                      {(stand.proposal.evidence ?? []).length === 0 ? (
                        <p className="rat">{t('Nothing was cited. This claim is asserted, not evidenced.')}</p>
                      ) : (
                        (stand.proposal.evidence ?? []).map((evidence, i) => (
                          <Ev
                            key={i}
                            src={
                              <>
                                <Att kind="quoted" />
                                {evidence.ref ? <Ref>{evidence.ref}</Ref> : <span>{t('no source named')}</span>}
                                <span className="sep">·</span>
                                {t(evidence.kind)}
                                {evidence.as_of && <><span className="sep">·</span><Fig>{fmtDate(evidence.as_of)}</Fig></>}
                              </>
                            }
                          >
                            {evidence.quote ? `« ${evidence.quote} »` : t('The citation carries a source but no quoted span.')}
                          </Ev>
                        ))
                      )}
                    </div>

                    {/* 5 — why it was proposed */}
                    <div className="sec">
                      <div className="lbl">
                        <span className="u-label">{t('Why it was proposed')}</span>
                        {isDedupe && dedupe && (
                          <span className="r">{tp(dedupe.reasons.length, '{n} reason', '{n} reasons')}</span>
                        )}
                      </div>
                      {isDedupe && dedupe ? (
                        dedupe.reasons.length === 0 ? (
                          <p className="rat">{t('The finder paired them without saying why. A score is not a reason.')}</p>
                        ) : (
                          dedupe.reasons.map((reason, i) => (
                            <div className="nb" key={`${reason.kind}-${i}`} style={{ marginTop: i ? '6px' : 0 }}>
                              {t(DEDUPE_REASON_WORD[reason.kind as DedupeReasonKind] ?? reason.kind)}
                              {reason.detail && (
                                <> <span className="sep">·</span> <span className="voice-record" style={{ fontSize: 'inherit' }}>{reason.detail}</span></>
                              )}
                            </div>
                          ))
                        )
                      ) : (
                        <p className="rat">
                          {rationaleOf(standPayload) || t('The filing carries no rationale. What stands behind it is the evidence above and nothing else.')}
                        </p>
                      )}
                    </div>

                    {/* 6 — the write preview */}
                    <div className="wpre">
                      <span className="u-label">{t('Holding this writes')}</span>{' '}
                      {writePreview(stand, t, tp, lang, mode === 'amend' ? effectivePayload : undefined)}
                    </div>
                  </div>

                  {/* the foot: one accent, and the mode rows that replace it */}
                  {mode === 'refuse' ? (
                    <div className="stfoot">
                      <span className="u-label">{t('Refuse because')}</span>
                      {REFUSE_REASONS.map((reason) => (
                        <button
                          type="button"
                          key={reason}
                          className="btn btn-sm"
                          disabled={busy}
                          onClick={() => { void refuse(stand.proposal.id, reason, reasonNote); }}
                        >
                          {t(REFUSE_REASON_LABEL[reason])}
                        </button>
                      ))}
                      <input
                        className="form-input"
                        style={{ width: '220px' }}
                        placeholder={t('a note for the run (optional)')}
                        value={reasonNote}
                        onChange={(e) => setReasonNote(e.target.value)}
                      />
                      <span className="spacer" />
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setMode(null)}>{t('Cancel')}</button>
                    </div>
                  ) : (
                    <div className="stfoot">
                      {mode === 'amend' ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-accent"
                            disabled={busy || stand.proposal.status !== 'unheld'}
                            onClick={() => { void hold(stand.proposal.id, amendedPayload()); }}
                          >
                            {t('Hold the amended text')} <span className="kbd">a</span>
                          </button>
                          <button type="button" className="btn btn-ghost" onClick={() => { setMode(null); setEdits({}); }}>
                            {t('Cancel')}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-accent"
                            disabled={busy || stand.proposal.status !== 'unheld'}
                            onClick={() => { void hold(stand.proposal.id, null); }}
                          >
                            {holdLabel} <span className="kbd">a</span>
                          </button>
                          {/* Round 7 §6.3: a birth cell and anything a broker asserted are never
                              held by the rule. The note beside the verb is not a warning about
                              the claim — it says who decides, which is P1 in one line. */}
                          {yourClick && (
                            <span
                              className="zone-note"
                              title={t('A birth year or decade, and any value a broker asserted, is never held on arrival: this one waits for a person, whatever the standing instruction says.')}
                            >
                              {t('always your click')}
                            </span>
                          )}
                          <button
                            type="button"
                            className="btn"
                            disabled={busy || stand.proposal.status !== 'unheld'}
                            onClick={() => setMode('amend')}
                          >
                            {t('Amend, then hold')} <span className="kbd">e</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={busy || stand.proposal.status !== 'unheld'}
                            onClick={() => setMode('refuse')}
                          >
                            {t('Refuse')} <span className="kbd">r</span>
                          </button>
                          <Lnk
                            disabled={busy || stand.proposal.status === 'held' || stand.proposal.status === 'amended'}
                            title={t('Remove the filing from the queue. Only an unheld or refused one may go.')}
                            onClick={() => setConfirmDelete(true)}
                          >
                            {t('Delete…')}
                          </Lnk>
                        </>
                      )}
                      <span className="spacer" />
                      <span className="voice-figure" style={{ color: 'var(--ink-3)' }}>
                        {t('{i} of {n}', { i: index >= 0 ? index + 1 : 0, n: rows.length })}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </Sheet>
        )}
      </Zone>

      {/* ── the session ledger ── */}
      {ledger.length > 0 && (
        <Zone>
          <ZoneHead
            label={t('This session')}
            count={ledger.length}
            note={t('what you disposed of since this server started · an undo reaches no further')}
          />
          <Sheet clip>
            {ledger.map((entry) => (
              <div className="row" key={`${entry.proposal_id}-${entry.at}`} style={{ gridTemplateColumns: '16px 96px 1fr auto auto' }}>
                {entry.verdict === 'refused' ? <span /> : <Att kind="held" />}
                <span className="k">{t(VERDICT_WORD[entry.verdict])}</span>
                <span>
                  {t(KIND_WORD[entry.kind] ?? entry.kind)}
                  {entry.subject_label ? <> <span className="sep">·</span> {entry.subject_label}</> : null}
                  {entry.refuse_reason ? <> <span className="sep">·</span> {t(REFUSE_REASON_LABEL[entry.refuse_reason])}</> : null}
                  {entry.reviewer_note ? <span className="d"> {entry.reviewer_note}</span> : null}
                  {/* The chained hold: this one click held an organisation too, and the undo
                      beside it reaches only this row. Both facts belong on the line. */}
                  {entry.also_held?.length ? (
                    <span className="d">
                      {' '}<span className="sep">·</span> {t('also held')}{' '}
                      {entry.also_held.map((a, i) => (
                        <Fragment key={a.id}>
                          {i > 0 ? <span className="sep">·</span> : null}
                          <Ref>{a.applied_ref ?? a.ref}</Ref>
                        </Fragment>
                      ))}{' '}
                      <span className="d">{t('(the undo does not walk it back)')}</span>
                    </span>
                  ) : null}
                </span>
                <span className="src-ref">{entry.applied_ref ?? entry.ref}</span>
                {entry.undoable ? (
                  <Lnk disabled={busy} onClick={() => { void undo(entry); }}>
                    {t('Undo')} <span className="kbd">u</span>
                  </Lnk>
                ) : entry.kind === 'dedupe' && dedupePayloadOf(entry.payload)?.action === 'merge' ? (
                  // A merge never enters the undo ledger: the archived row keeps `merged_into`,
                  // and unpicking it is a fresh decision. Saying "undone" here would be a lie.
                  <span className="d" title={t('the archived organisation keeps merged_into')}>{t('cannot be undone here')}</span>
                ) : (
                  <span className="d">{t('undone')}</span>
                )}
              </div>
            ))}
          </Sheet>
        </Zone>
      )}

      {/* WHAT HE IS ABOUT TO OVERRULE, in words and counts, before the click (08.09.2026). The one
          consequence that cannot be walked back is named on its own line: a person held over a name
          match becomes a SECOND row for the same human, and this record has no merge for people. */}
      <ConfirmModal
        open={overrideOpen}
        title={t('Hold the {n} the rule left?', { n: leftBehind?.total ?? 0 })}
        confirmLabel={t('Hold them all')}
        onCancel={() => setOverrideOpen(false)}
        onConfirm={() => { void bulkHold(true); }}
      >
        <p className="read">{t('Each of these was left because the record already answers it. Holding them writes every one through the same applier, and the objection it overruled is written on the row.')}</p>
        <Sheet pad className="facts">
          {(leftBehind?.reasons ?? []).map(([why, n]) => (
            <div className="fact" key={why}>
              <span className="fk"><Fig>{n}</Fig></span>
              <span className="d">{why}</span>
              <span className="fmeta" />
            </div>
          ))}
        </Sheet>
        <p className="zone-empty" style={{ marginTop: 12 }}>
          {t('One of these is the expensive one: a person whose name is already on the record becomes a SECOND row for the same human. A merge now exists (scripts/person-merge-dry-run.ts) and joins two rows onto one survivor, so it is no longer irreversible — but it is a script a person runs and reads, not a click here. Everything else supersedes, chains or can be archived. Narrow the Kind filter first if you want to hold the rest without the people.')}
        </p>
      </ConfirmModal>

      {/* THE AUTO-ACCEPT PANEL. Every class carries its live count AND what waiving it costs, side
          by side — a switch whose consequence is not written next to it is not a decision he can
          make. The two classes no switch reaches are listed at the foot, so the page never implies
          that flipping everything empties the queue. */}
      <ConfirmModal
        open={autoHoldOpen}
        title={t('What may be held without asking you?')}
        confirmLabel={t('Done')}
        onCancel={() => setAutoHoldOpen(false)}
        onConfirm={() => { setAutoHoldOpen(false); void refresh(); }}
      >
        {autoHoldBusy && !autoHold ? (
          <p className="read">{t('Asking the rule about every unheld row…')}</p>
        ) : autoHold ? (
          <>
            <p className="read">
              {t('{unheld} unheld · {holdable} would be held by a plain Hold all today. Each switch below turns off ONE kind of judgement, for this backlog and for everything that arrives after it.', { unheld: autoHold.unheld, holdable: autoHold.holdable })}
            </p>
            <Sheet pad className="facts">
              {autoHold.guards.map((g) => (
                <div className="fact" key={g.guard}>
                  <span className="fk">
                    <label>
                      <input
                        type="checkbox"
                        checked={g.waived}
                        disabled={autoHoldBusy}
                        onChange={() => { void toggleGuard(g.guard); }}
                      />{' '}
                      <Fig>{g.count}</Fig>
                    </label>
                  </span>
                  <span className="d">
                    <strong>{t(GUARD_LABEL[g.guard] ?? g.guard)}</strong>
                    <br />
                    {t(g.cost)}
                  </span>
                  <span className="fmeta">{g.waived ? t('held without asking') : t('needs you')}</span>
                </div>
              ))}
            </Sheet>
            <p className="zone-empty" style={{ marginTop: 12 }}>
              {t('Two guards have no switch and never will: birth data filed into a cell that is not the birth cell, and a filing naming no cell, subject or counterpart. Holding either writes something no reader of that cell expects.')}
              {' '}
              {autoHold.never_waivable.map((n) => `${n.count}`).join(' + ')}
              {' '}
              {t('rows are guarded that way now.')}
            </p>
          </>
        ) : null}
      </ConfirmModal>

      <ConfirmModal
        open={confirmDelete}
        title={t('Delete this filing?')}
        danger
        confirmLabel={t('Delete it')}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => { if (current) void remove(current.id); }}
      >
        <p className="rat">
          {t('The proposal leaves the queue for good, with its payload and its evidence. The audit log keeps what it said. Nothing in the record changes.')}
        </p>
      </ConfirmModal>
    </PageShell>
  );
}
